<template>
  <div class="outreach-contact-engagement">
    <BaseOutreachContactStat
      v-if="!hasAvgTotalEngagement"
      :tooltip="
        getLocaleText('outreach_contact_engagement.no_avg_engagement_tooltip')
      "
    >
      <div class="outreach-contact-engagement__no-value">
        {{ avgTotalEngagementFormatted }}
      </div>
    </BaseOutreachContactStat>

    <BaseButtonDropdown
      v-else
      interaction="hover"
      new-design
      new-dropdown
    >
      <template #btn="{ listeners }">
        <BaseOutreachContactStat v-on="listeners">
          {{ avgTotalEngagementFormatted }}
        </BaseOutreachContactStat>
      </template>
      <div class="outreach-contact-engagement__stats-container">
        <div class="outreach-contact-engagement__stats-title">
          {{ getLocaleText("outreach_contact_engagement.avg_engagement") }}
        </div>

        <div class="outreach-contact-engagement__stats-row">
          <div
            v-for="stat in stats"
            :key="stat.id"
            class="outreach-contact-engagement__stat"
          >
            <QIcon
              class="outreach-contact-engagement__stat-icon"
              :name="stat.icon"
            />

            <div
              class="outreach-contact-engagement__stat-value"
              :class="`outreach-contact-engagement__stat-${stat.id}`"
            >
              {{ stat.value }}
            </div>
          </div>
        </div>
      </div>
    </BaseButtonDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButtonDropdown } from "shared/components/base";
import BaseOutreachContactStat from "shared/components/Outreach/OutreachContact/BaseOutreachContactStat.vue";
import type { ContactType } from "shared/composables/useContact";
import { SOCIAL_ICONS } from "shared/constants";
import { prettyNumber } from "shared/helpers/number";
import { type Nullable } from "shared/types";
import type { Influencer } from "shared/types/outreach";

interface OutreachContactEngagementProps {
  contact: ContactType;
}

const props = defineProps<OutreachContactEngagementProps>();

function formatNumber(number: Nullable<number>): string {
  return String(prettyNumber(Number(number).toFixed(), "suffix"));
}

function hasEngagementStats(contact: ContactType): contact is Influencer {
  return [
    "average_facebook_engagement",
    "average_pinterest_shares",
    "average_reddit_engagements",
    "average_total_engagements",
    "average_twitter_shares",
  ].every((key) => key in contact);
}

const engagementContact = computed<Nullable<Influencer>>(() =>
  hasEngagementStats(props.contact) ? props.contact : null
);

const avgTotalEngagement = computed<number>(
  () => engagementContact.value?.average_total_engagements ?? 0
);

const hasAvgTotalEngagement = computed<boolean>(
  () => avgTotalEngagement.value > 0
);

const avgTotalEngagementFormatted = computed<string>(() =>
  formatNumber(avgTotalEngagement.value)
);

const stats = computed(() => {
  const contact = engagementContact.value;

  if (!contact) {
    return [];
  }

  return [
    {
      id: "facebook",
      icon: SOCIAL_ICONS.Facebook,
      value: formatNumber(contact.average_facebook_engagement),
    },
    {
      id: "twitter",
      icon: SOCIAL_ICONS.Twitter,
      value: formatNumber(contact.average_twitter_shares),
    },
    {
      id: "reddit",
      icon: SOCIAL_ICONS.Reddit,
      value: formatNumber(contact.average_reddit_engagements),
    },
    {
      id: "pinterest",
      icon: SOCIAL_ICONS.Pinterest,
      value: formatNumber(contact.average_pinterest_shares),
    },
  ];
});
</script>

<style lang="scss" scoped>
.outreach-contact-engagement {
  font-weight: 500;
  line-height: 150%;

  &__no-value {
    color: var(--s-color-denim-5);
    text-decoration: none;
  }

  &__stats {
    &-container {
      display: flex;
      flex-direction: column;
      gap: var(--s-spacing-lg);
      padding: var(--s-spacing-lg);
    }

    &-title {
      font-weight: var(--s-font-weight-bold);
      line-height: 150%;
      color: var(--s-color-denim-9);
    }

    &-row {
      display: flex;
      flex: 1;
      justify-content: space-between;
      gap: 24px;
    }
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;

    &-icon {
      font-size: 24px;
    }

    &-value {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      background-color: var(--s-color-denim-6);
      color: $white;
      border-radius: 50%;
      font-weight: 500;
    }

    &-facebook {
      background-color: #3662e3;
    }

    &-twitter {
      background-color: #000;
    }

    &-reddit {
      background-color: #d9622b;
    }

    &-pinterest {
      background-color: #ca3a31;
    }
  }
}
</style>
